<template>
  <div :class="fieldConfig.class || 'p-field p-col-12 p-md-4'" v-if="fieldConfig.type !== 'hidden'">

    <label :for="fieldKey">
      {{ fieldConfig.label }}
      <span v-if="fieldConfig.required" class="p-text-danger">*</span>
    </label>

    <template v-if="canEdit">

      <AppAutocomplete
        v-if="fieldConfig.type === 'clientAutocomplete'"
        :fieldId="fieldKey"
        :optionLabel="getClientLabel"
        optionValue="clientCompositeKey"
        v-model="localValue"
        :editModel="getCurrentClient"
        :fetchFunction="fetchClients"
        placeholder="Selecionar cliente"
        @selection-change="handleChange"
        @clear-selection="handleClear"
        appendTo="body"
      >
        <template #option="slotProps">
          <div>
            <b>{{ slotProps.option.name }}</b><br />
            <span style="white-space: normal; word-break: break-word;">
              <span v-if="slotProps.option.email">EMAIL: {{ slotProps.option.email }} | </span>
              <span v-if="slotProps.option.fiscalNumber">NIF: {{ slotProps.option.fiscalNumber }}</span>
            </span>
          </div>
        </template>
      </AppAutocomplete>

      <AppAutocomplete
        v-else-if="fieldConfig.type === 'supplierAutocomplete'"
        :fieldId="fieldKey"
        :optionLabel="getSupplierLabel"
        optionValue="id"
        v-model="localValue"
        :editModel="getCurrentSupplier"
        :fetchFunction="fetchSuppliers"
        placeholder="Selecionar Fornecedor"
        @selection-change="handleChange"
        @clear-selection="handleClear"
        appendTo="body"
      >
        <template #option="slotProps">
          <div>
            <b>{{ slotProps.option.name }}</b><br />
            <span style="white-space: normal; word-break: break-word;">
              <span v-if="slotProps.option.type">NIF: {{ slotProps.option.nif }} | </span>
              <span v-if="slotProps.option.type">Tipo: {{ slotProps.option.type }}</span>
            </span>
          </div>
        </template>
      </AppAutocomplete>

      <AppAutocomplete
        v-else-if="fieldConfig.type === 'patAutocomplete'"
        :fieldId="fieldKey"
        :optionLabel="getPatLabel"
        optionValue="id"
        v-model="localValue"
        :editModel="getCurrentPat"
        :fetchFunction="fetchPats"
        placeholder="Selecionar Pat"
        @selection-change="handleChange"
        @clear-selection="handleClear"
        appendTo="body"
      >
        <template #option="slotProps">
          <div>
            <b>{{ slotProps.option.client.name }}</b><br />
            <span style="white-space: normal; word-break: break-word;">
              <span v-if="slotProps.option.id">PAT: {{ slotProps.option.id }} | </span>
              <span v-if="slotProps.option.resume">{{ slotProps.option.resume }}</span>
            </span>
          </div>
        </template>
      </AppAutocomplete>

      <AppTransportDocumentField v-else-if="fieldConfig.type === 'transportDocumentField'"
        :fieldId="fieldKey"
        :form="form"
        @field-change="handleTransportFieldChange"
      />

      <Calendar
        v-else-if="fieldConfig.type === 'date'"
        :id="fieldKey"
        v-model="localValue"
        v-bind="fieldProps"
        :class="{ 'p-invalid': error }"
        @input="$emit('input', localValue)"
      />

      <div class="p-inputgroup" v-else-if="fieldConfig.type === 'time'">
        <span class="p-inputgroup-addon">
          <i class="pi pi-clock"></i>
        </span>
        <span class="p-float-label">
          <Calendar
            :id="fieldKey"
            v-model="localValue"
            v-bind="fieldProps"
            v-bind:class="[
              { 'p-error': error },
              'form-control',
            ]"
            @input="$emit('input', localValue)"
          />
        </span>
      </div>

      <Textarea
        v-else-if="fieldConfig.type === 'textarea'"
        :id="fieldKey"
        v-model="localValue"
        v-bind="fieldProps"
        :class="{ 'p-invalid': error }"
        @input="$emit('input', localValue)"
      />

      <InputNumber
        v-else-if="['number', 'currency'].includes(fieldConfig.type)"
        :id="fieldKey"
        v-model="localValue"
        v-bind="fieldProps"
        :class="{ 'p-invalid': error }"
        @input="$emit('input', localValue)"
      />

      <Dropdown
        v-else-if="fieldConfig.type === 'select'"
        :id="fieldKey"
        v-model="localValue"
        :options="options"
        v-bind="fieldProps"
        :class="{ 'p-invalid': error }"
        @change="$emit('input', localValue)"
      />

      <Checkbox
        v-else-if="fieldConfig.type === 'checkbox'"
        :id="fieldKey"
        v-model="localValue"
        v-bind="fieldProps"
        binary
        :class="{ 'p-invalid': error }"
        class="p-ml-2"
        style="display: flex;"
        @change="$emit('input', localValue)"
      />

      <InputText
        v-else
        :id="fieldKey"
        v-model="localValue"
        v-bind="fieldProps"
        :class="{ 'p-invalid': error }"
        @input="$emit('input', localValue)"
        :placeholder="fieldConfig.auto_generate ? '(senão preencher, gera automaticamente)' : ''"
      />

      <small v-if="error" class="p-error">{{ error }}</small>
    </template>
    <template v-else>
      <div v-if="fieldConfig.type === 'transportDocumentField'">
        <AppTransportDocumentField
          :fieldId="fieldKey"
          :form="form"
          :read-only="true"
        />
      </div>
      <div v-else>{{ getFormatter(localValue, fieldConfig) }}</div>
    </template>
  </div>
</template>

<script>
import AppAutocomplete from '@/components/elements/AppAutocomplete.vue';
import AppTransportDocumentField from './AppTransportDocumentField.vue';
import clientService from "@/services/client.service";
import supplierService from '@/services/supplier.service';
import patsService from '@/services/pat.service';
import { getFieldProps } from './fieldComponents';

export default {
  name: 'AppDynamicField',
  components: { AppAutocomplete, AppTransportDocumentField },
  props: {
    fieldKey: {
      type: String,
      required: true
    },
    fieldConfig: {
      type: Object,
      required: true
    },
    value: {
      required: true
    },
    error: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    form: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      localValue: this.value,
    };
  },
  computed: {
    fieldProps() {
      return getFieldProps(this.fieldConfig);
    }
  },
  watch: {
    value(newVal) {
      this.localValue = newVal;
    }
  },
  methods: {
    async fetchPats(search = '') {
      try {
        let pats = await patsService.query({ 'fechado': 0, 'search': search });

        return pats;
      } catch (error) {
        console.error('Error fetching PATs:', error);
        throw error;
      }
    },
    async getCurrentPat(no) {
      if (!no || no === '0') {
        return null;
      }

      try {
        let pats = await patsService.query({ 'nopat': no });

        if (!pats.length) {
          return null;
        }

        return pats[0];
      } catch (error) {
        console.error('Error fetching PATs:', error);
        throw error;
      }
    },
    getPatLabel(option) {
      if (!option) return '';
      return `${option.id} | ${option.client.name || ''}`;
    },
    async fetchSuppliers(search = '') {
      try {
        return await supplierService.query({ search });
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        return [];
      }
    },
    getSupplierLabel(supplier) {
      if (!supplier) return '';
      return `${supplier.name} | NIF: ${supplier.nif ?? supplier.fiscalNumber}`;
    },
    async getCurrentSupplier(no) {
      try {
        const response = await supplierService.query({ no: no });
        return response.length ? response[0] : null;
      } catch (error) {
        console.error('Error fetching client:', error);
        return null;
      }
    },
    async fetchClients(search = '') {
      try {
        return await clientService.query({ search });
      } catch (error) {
        console.error('Error fetching clients:', error);
        return [];
      }
    },
    async getCurrentClient(compositeValue) {
      if (!compositeValue && compositeValue !== 0) return null;
      try {
        const id = String(compositeValue).includes('-') ? String(compositeValue).split('-')[0] : compositeValue;
        const estab = String(compositeValue).includes('-') ? String(compositeValue).split('-')[1] : 0;
        const response = await clientService.query({ no: id, estab });
        if (!response.length) return null;
        return {
          ...response[0],
          clientCompositeKey: `${response[0].id}-${response[0].estab ?? 0}`
        };
      } catch (error) {
        console.error('Error fetching client:', error);
        return null;
      }
    },
    getClientLabel(client) {
      if (!client) return '';
      return `${client.name} | EMAIL: ${client.email} | NIF: ${client.fiscalNumber}`;
    },
    async handleChange(clientNo) {
      this.$emit('input', clientNo);
    },
    handleClear() {
      this.$emit('input', null);
    },
    getFormatter(value) {
      switch (typeof value) {
        case 'boolean':
          return value ? 'Sim' : 'Não';
        default:
          return value;
      }
    },
    handleTransportFieldChange({ key, value }) {
      this.$emit('update-form-data', { [key]: value });
    }
  }
};
</script>